import { useState } from 'react';

import callAllFn from '@julofinance/web-helpers/dist/fn/callAllFn';

import { ApiResult } from '@models/api';
import { useMutation, UseMutationOptions } from '@hooks/react-query';
import { CustomError, fetcherMutation } from 'utils/react-query';
import { useSnackbar } from '@new-components/elements';

import { VerifyBankAccountParam } from '../models/mutations';

function useRVerifyBankAccount(
  options: UseMutationOptions<
    ApiResult<unknown>,
    Record<string, unknown>,
    VerifyBankAccountParam
  > = {},
) {
  const { onSuccess, onError, onMutate, onSettled, ...resOptions } = options;

  const [isVerifiedBankAccount, setIsVerifiedBankAccount] = useState(false);
  const [isInvalidBankAccount, setIsInvalidBankAccount] = useState(false);
  const [isValidatedBankAccount, setIsValidatedBankAccount] = useState(false);

  const { onOpen } = useSnackbar();

  const mutationReturns = useMutation<
    ApiResult<unknown>,
    Record<string, unknown>,
    VerifyBankAccountParam
  >({
    mutationFn: async ({ context, variables }) =>
      await fetcherMutation({
        context: {
          ...context,
          method: 'POST',
          path: 'application/verify-grab-bank-account',
        },
        variables,
      }),
    onMutate: callAllFn(() => {
      setIsValidatedBankAccount(false);
    }, onMutate),
    onSettled: callAllFn(() => {
      setIsValidatedBankAccount(true);
    }, onSettled),
    onSuccess: callAllFn(() => {
      setIsVerifiedBankAccount(true);
      setIsInvalidBankAccount(false);
    }, onSuccess),
    onError: callAllFn((err: CustomError) => {
      const statusCode = err.payload?.statusCode;
      switch (statusCode) {
        case 400:
          setIsVerifiedBankAccount(false);
          setIsInvalidBankAccount(true);
          break;

        default:
          onOpen({
            message:
              (err.payload?.errors as Array<string>)?.[0] ||
              'Opps, Something went wrong.',
            severity: 'error',
          });
          setIsValidatedBankAccount(false);
          break;
      }
    }, onError),
    ...resOptions,
  });

  return {
    isVerifiedBankAccount,
    isInvalidBankAccount,
    isValidatedBankAccount,
    ...mutationReturns,
  };
}

export default useRVerifyBankAccount;
